import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { investorContactAPI, teamAPI, urgencyRulesAPI } from '../services/api';
import { ArrowLeft, Edit, Clock, AlertTriangle, MessageSquare, Plus, Trash2, Save, X, Briefcase } from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import { useDateFormat } from '../hooks/useDateFormat';

const levelColorsByIndex = [
  'bg-blue-100 text-blue-700',
  'bg-yellow-100 text-yellow-700',
  'bg-orange-100 text-orange-700',
  'bg-red-100 text-red-700',
  'bg-purple-100 text-purple-700',
  'bg-pink-100 text-pink-700',
];

const InvestorContactDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canEdit, canDelete } = usePermissions() || {};
  const fmtDate = useDateFormat();
  const [contact, setContact] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [urgencyLevels, setUrgencyLevels] = useState(['Low', 'Medium', 'High', 'Very High']);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');
  const [investmentSummary, setInvestmentSummary] = useState([]);

  // Investment Summary controls (same as Investor Account)
  const [isCurrencyFormat, setIsCurrencyFormat] = useState('full');
  const [isSelectedCurrency, setIsSelectedCurrency] = useState('INR');
  const [isExchangeRate, setIsExchangeRate] = useState(83.5);

  // Interaction form state
  const [showInteractionForm, setShowInteractionForm] = useState(false);
  const [editingInteraction, setEditingInteraction] = useState(null);
  const [interactionForm, setInteractionForm] = useState({
    date_of_interaction: new Date().toISOString().split('T')[0],
    details: '',
    next_steps: '',
    investor_lead_interacted_ids: [],
  });

  const getUrgencyColor = (urgency) => {
    const idx = urgencyLevels.indexOf(urgency);
    if (idx === -1) return 'bg-gray-100 text-gray-600';
    return levelColorsByIndex[idx % levelColorsByIndex.length];
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [contactRes, interactionsRes, teamRes, rulesRes, invSummaryRes] = await Promise.all([
        investorContactAPI.getById(id),
        investorContactAPI.getInteractions(id),
        teamAPI.getMembers(),
        urgencyRulesAPI.get(),
        investorContactAPI.getInvestmentSummary(id).catch(() => ({ data: [] })),
      ]);
      setContact(contactRes.data);
      setInteractions(interactionsRes.data);
      setTeamMembers(teamRes.data);
      setUrgencyLevels(rulesRes.data.urgency_levels || ['Low', 'Medium', 'High', 'Very High']);
      setInvestmentSummary(invSummaryRes.data || []);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetInteractionForm = () => {
    setInteractionForm({
      date_of_interaction: new Date().toISOString().split('T')[0],
      details: '',
      next_steps: '',
      investor_lead_interacted_ids: [],
    });
    setEditingInteraction(null);
    setShowInteractionForm(false);
  };

  const handleSaveInteraction = async () => {
    if (!interactionForm.date_of_interaction) return;
    try {
      if (editingInteraction) {
        await investorContactAPI.updateInteraction(id, editingInteraction.id, interactionForm);
      } else {
        await investorContactAPI.createInteraction(id, interactionForm);
      }
      // Reload data
      const [contactRes, intRes] = await Promise.all([
        investorContactAPI.getById(id),
        investorContactAPI.getInteractions(id),
      ]);
      setContact(contactRes.data);
      setInteractions(intRes.data);
      resetInteractionForm();
    } catch (err) {
      console.error('Failed to save interaction:', err);
      alert('Failed to save interaction');
    }
  };

  const handleDeleteInteraction = async (interactionId) => {
    if (!window.confirm('Delete this interaction?')) return;
    try {
      await investorContactAPI.deleteInteraction(id, interactionId);
      setInteractions(prev => prev.filter(i => i.id !== interactionId));
      // Reload contact for updated days_since
      const contactRes = await investorContactAPI.getById(id);
      setContact(contactRes.data);
    } catch (err) {
      console.error('Failed to delete interaction:', err);
    }
  };

  const handleEditInteraction = (interaction) => {
    setEditingInteraction(interaction);
    // Prefer the new plural list; fall back to the singular legacy field so
    // interactions logged before v2 still populate the picker correctly.
    const leadIds = (interaction.investor_lead_interacted_ids && interaction.investor_lead_interacted_ids.length > 0)
      ? interaction.investor_lead_interacted_ids
      : (interaction.investor_lead_interacted_id ? [interaction.investor_lead_interacted_id] : []);
    setInteractionForm({
      date_of_interaction: interaction.date_of_interaction || '',
      details: interaction.details || '',
      next_steps: interaction.next_steps || '',
      investor_lead_interacted_ids: leadIds,
    });
    setShowInteractionForm(true);
  };

  if (loading) return <div className="text-center py-12">Loading...</div>;
  if (!contact) return <div className="text-center py-12">Contact not found</div>;

  const fullName = [contact.salutation, contact.first_name, contact.middle_name, contact.last_name].filter(Boolean).join(' ');

  const tabs = [
    { id: 'info', label: 'Contact Information' },
    { id: 'coverage', label: 'Fundraise Coverage' },
    { id: 'interactions', label: 'Interactions' },
    { id: 'investment_summary', label: 'Investment Summary' },
  ];

  return (
    <div data-testid="investor-contact-details-page">
      <button
        onClick={() => navigate('/investors/contacts')}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-6"
        data-testid="back-to-contacts-btn"
      >
        <ArrowLeft size={20} className="mr-2" />
        Back to Investor Contacts
      </button>

      {/* Header */}
      <div className="bg-white rounded-xl shadow-md mb-6">
        <div className="p-6 border-b flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900" data-testid="contact-name">{fullName}</h1>
            <p className="text-gray-500 mt-1">{contact.email}</p>
            <div className="flex gap-3 mt-2">
              {contact.days_since_last_interaction != null && (
                <span className="inline-flex items-center gap-1 text-sm text-gray-600 bg-gray-100 px-2 py-1 rounded">
                  <Clock size={14} /> {contact.days_since_last_interaction} days since last interaction
                </span>
              )}
              <span className={`text-xs font-medium px-2 py-1 rounded-full ${getUrgencyColor(contact.coverage_urgency)}`}>
                {contact.coverage_urgency || '-'} Urgency
              </span>
            </div>
          </div>
          {canEdit && (
            <button
              onClick={() => navigate(`/investors/contacts/${id}/edit`)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              data-testid="edit-contact-btn"
            >
              <Edit size={16} className="mr-2" />
              Edit
            </button>
          )}
        </div>

        {/* Tabs */}
        <div className="border-b">
          <div className="flex overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'info' && (
            <div data-testid="tab-content-info">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <InfoField label="Salutation" value={contact.salutation} />
                <InfoField label="First Name" value={contact.first_name} />
                <InfoField label="Middle Name" value={contact.middle_name} />
                <InfoField label="Last Name" value={contact.last_name} />
                <InfoField
                  label="Email"
                  value={[contact.email, ...(contact.additional_emails || [])].filter(Boolean).join(', ') || '-'}
                />
                <InfoField
                  label="Mobile"
                  value={(() => {
                    const primary = contact.mobile
                      ? `${contact.mobile_country_code || ''} ${contact.mobile}`.trim()
                      : '';
                    const extras = (contact.additional_mobiles || [])
                      .filter((m) => m && m.number)
                      .map((m) => `${m.country_code || ''} ${m.number}`.trim());
                    const all = [primary, ...extras].filter(Boolean);
                    return all.length ? all.join(', ') : '-';
                  })()}
                />
                <InfoField
                  label="Telephone"
                  value={[contact.telephone, ...(contact.additional_telephones || [])].filter(Boolean).join(', ') || '-'}
                />
                <InfoField label="WhatsApp Opt-out" value={contact.whatsapp_opt_out} />
                <InfoField
                  label="Touch Base Required"
                  value={contact.touch_base_required || 'Yes'}
                />
                <InfoField label="Distributor" value={contact.distributor_name} />
                <InfoField label="Investor Lead" value={contact.investor_lead_name} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <span className="text-sm text-gray-500">Brief Profile</span>
                  <p className="mt-1 text-gray-900 whitespace-pre-wrap">{contact.brief_profile || '-'}</p>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Educational Background</span>
                  <p className="mt-1 text-gray-900 whitespace-pre-wrap">{contact.educational_background || '-'}</p>
                </div>
              </div>
              {/* Remarks — internal-only free-form notes. Full width so long
                  entries render without awkward wrapping. */}
              <div className="mt-6">
                <span className="text-sm text-gray-500">Remarks</span>
                <p
                  className="mt-1 text-gray-900 whitespace-pre-wrap"
                  data-testid="contact-remarks-display"
                >
                  {contact.remarks || '-'}
                </p>
              </div>
            </div>
          )}

          {activeTab === 'coverage' && (
            <div data-testid="tab-content-coverage">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <span className="text-sm text-gray-500 block mb-1">Days Since Last Interaction</span>
                  <span className="text-2xl font-bold text-gray-900">
                    {contact.days_since_last_interaction != null ? contact.days_since_last_interaction : '-'}
                  </span>
                  {contact.last_interaction_date && (
                    <p className="text-xs text-gray-400 mt-1">Last: {contact.last_interaction_date}</p>
                  )}
                </div>
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <span className="text-sm text-gray-500 block mb-1">Touch Base Required</span>
                  <span className={`text-lg font-semibold ${contact.touch_base_required === 'Yes' ? 'text-orange-600' : 'text-green-600'}`}>
                    {contact.touch_base_required || 'No'}
                  </span>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 border">
                  <span className="text-sm text-gray-500 block mb-1">Coverage Urgency</span>
                  <span className={`inline-block text-sm font-medium px-3 py-1 rounded-full ${getUrgencyColor(contact.coverage_urgency)}`}>
                    {contact.coverage_urgency || '-'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'interactions' && (
            <div data-testid="tab-content-interactions">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Details of Interactions</h3>
                {/* Adding an interaction is available to every authenticated
                    role (including Viewer) — logging touch-points is a
                    read-heavy activity the business explicitly wants
                    everyone to participate in. Edit / Delete remain gated
                    by `canEdit` / `canDelete` on each row below. */}
                <button
                  onClick={() => { resetInteractionForm(); setShowInteractionForm(true); }}
                  className="inline-flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
                  data-testid="add-interaction-btn"
                >
                  <Plus size={16} className="mr-1" />
                  Add Interaction
                </button>
              </div>

              {/* Interaction Form */}
              {showInteractionForm && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4" data-testid="interaction-form">
                  <h4 className="font-medium text-gray-800 mb-3">
                    {editingInteraction ? 'Edit Interaction' : 'New Interaction'}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Date of Interaction *</label>
                      <input
                        type="date"
                        value={interactionForm.date_of_interaction}
                        onChange={e => setInteractionForm(prev => ({ ...prev, date_of_interaction: e.target.value }))}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                        data-testid="interaction-date-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Investor Lead(s) Interacted
                        <span className="ml-1 text-[10px] font-normal text-gray-500">(select one or more)</span>
                      </label>
                      {(() => {
                        const selectedIds = interactionForm.investor_lead_interacted_ids || [];
                        const toggleLead = (uid) => {
                          setInteractionForm(prev => {
                            const cur = prev.investor_lead_interacted_ids || [];
                            const next = cur.includes(uid) ? cur.filter(x => x !== uid) : [...cur, uid];
                            return { ...prev, investor_lead_interacted_ids: next };
                          });
                        };
                        return (
                          <div
                            className="w-full border border-gray-300 rounded-lg px-2 py-2 focus-within:ring-2 focus-within:ring-blue-500 max-h-48 overflow-y-auto bg-white"
                            data-testid="interaction-lead-multiselect"
                          >
                            {teamMembers.length === 0 ? (
                              <div className="px-2 py-1 text-xs text-gray-500">No team members available</div>
                            ) : teamMembers.map(m => {
                              const picked = selectedIds.includes(m.id);
                              return (
                                <label
                                  key={m.id}
                                  className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-sm ${picked ? 'bg-blue-50 text-blue-900' : 'hover:bg-gray-50 text-gray-700'}`}
                                  data-testid={`interaction-lead-option-${m.id}`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={picked}
                                    onChange={() => toggleLead(m.id)}
                                    className="h-4 w-4 text-blue-600 rounded"
                                    data-testid={`interaction-lead-checkbox-${m.id}`}
                                  />
                                  <span className="flex-1 truncate">{m.name}</span>
                                  {m.email && <span className="text-[10px] text-gray-500 truncate max-w-[140px]">{m.email}</span>}
                                </label>
                              );
                            })}
                            {selectedIds.length > 0 && (
                              <div className="mt-1 px-2 pt-1 border-t border-gray-100 text-[10px] text-gray-500">
                                {selectedIds.length} selected
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Details about Interaction</label>
                      <textarea
                        value={interactionForm.details}
                        onChange={e => setInteractionForm(prev => ({ ...prev, details: e.target.value }))}
                        rows={3}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                        data-testid="interaction-details-textarea"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Next Steps</label>
                      <textarea
                        value={interactionForm.next_steps}
                        onChange={e => setInteractionForm(prev => ({ ...prev, next_steps: e.target.value }))}
                        rows={2}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                        data-testid="interaction-next-steps-textarea"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 mt-4">
                    <button
                      type="button"
                      onClick={resetInteractionForm}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveInteraction}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm inline-flex items-center"
                      data-testid="save-interaction-btn"
                    >
                      <Save size={16} className="mr-1" />
                      {editingInteraction ? 'Update' : 'Save'}
                    </button>
                  </div>
                </div>
              )}

              {/* Interactions List */}
              {interactions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <MessageSquare size={40} className="mx-auto mb-2 text-gray-300" />
                  <p>No interactions recorded yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {interactions.map(interaction => (
                    <div key={interaction.id} className="bg-white border rounded-lg p-4 hover:border-blue-200 transition-colors" data-testid={`interaction-${interaction.id}`}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <span className="text-sm font-medium text-blue-600">
                              {fmtDate(interaction.date_of_interaction)}
                            </span>
                            {(() => {
                              // Prefer the new plural list; fall back to legacy singular.
                              const names = (interaction.investor_lead_interacted_names && interaction.investor_lead_interacted_names.length > 0)
                                ? interaction.investor_lead_interacted_names
                                : (interaction.investor_lead_interacted_name ? [interaction.investor_lead_interacted_name] : []);
                              if (names.length === 0) return null;
                              return (
                                <>
                                  <span className="text-[10px] text-gray-500 uppercase tracking-wider">Leads:</span>
                                  {names.map((n, idx) => (
                                    <span
                                      key={`${n}-${idx}`}
                                      className="text-xs text-blue-800 bg-blue-100 px-2 py-0.5 rounded-full border border-blue-200"
                                      data-testid={`interaction-lead-chip-${interaction.id}-${idx}`}
                                    >
                                      {n}
                                    </span>
                                  ))}
                                </>
                              );
                            })()}
                          </div>
                          {interaction.details && (
                            <p className="text-gray-700 text-sm whitespace-pre-wrap">{interaction.details}</p>
                          )}
                          {interaction.next_steps && (
                            <div className="mt-2 text-sm">
                              <span className="text-gray-500 font-medium">Next Steps: </span>
                              <span className="text-gray-700">{interaction.next_steps}</span>
                            </div>
                          )}
                        </div>
                        {canEdit && (
                          <div className="flex gap-1 ml-3">
                            <button
                              onClick={() => handleEditInteraction(interaction)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-50"
                              data-testid={`edit-interaction-${interaction.id}`}
                            >
                              <Edit size={14} />
                            </button>
                            {canDelete && (
                              <button
                                onClick={() => handleDeleteInteraction(interaction.id)}
                                className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50"
                                data-testid={`delete-interaction-${interaction.id}`}
                              >
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'investment_summary' && (() => {
            // Currency conversion helpers (same logic as Investor Account)
            const convertAmt = (amount, srcCcy) => {
              if (amount == null) return 0;
              const s = (srcCcy || 'INR').toUpperCase(), t = isSelectedCurrency.toUpperCase();
              if (s === t) return amount;
              if (s === 'INR' && t === 'USD') return amount / isExchangeRate;
              if (s === 'USD' && t === 'INR') return amount * isExchangeRate;
              return amount;
            };
            const fmtDisplay = (val) => {
              if (val == null) return '-';
              const sym = isSelectedCurrency === 'INR' ? '\u20B9' : '$';
              const locale = isSelectedCurrency === 'INR' ? 'en-IN' : 'en-US';
              switch (isCurrencyFormat) {
                case 'crores': return `${sym}${(val / 10000000).toFixed(2)} Cr`;
                case 'millions': return `${sym}${(val / 1000000).toFixed(2)} M`;
                default: return new Intl.NumberFormat(locale, { style: 'currency', currency: isSelectedCurrency, maximumFractionDigits: 0 }).format(val);
              }
            };
            const fmtAmt = (amount, ccy) => fmtDisplay(convertAmt(Number(amount || 0), ccy));

            // Grand totals across ALL accounts (currency-aware)
            const allInvestments = investmentSummary.flatMap(a => (a.investments || []).map(inv => inv));
            const grandTotals = allInvestments.reduce((acc, inv) => ({
              commitment: acc.commitment + convertAmt(inv.commitment_amount || 0, inv.fund_currency),
              called: acc.called + convertAmt(inv.called_amount || 0, inv.fund_currency),
              contribution: acc.contribution + convertAmt(inv.contribution_amount || 0, inv.fund_currency),
              distribution: acc.distribution + convertAmt(inv.distribution_amount || 0, inv.fund_currency),
              nav: acc.nav + convertAmt(inv.nav_value || 0, inv.fund_currency),
            }), { commitment: 0, called: 0, contribution: 0, distribution: 0, nav: 0 });

            // Detect multiple currencies
            const allCurrencies = [...new Set(allInvestments.map(i => i.fund_currency).filter(Boolean))];
            const showConverter = allCurrencies.length > 1;

            return (
            <div data-testid="tab-content-investment-summary">
              {investmentSummary.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Briefcase size={40} className="mx-auto mb-2 text-gray-300" />
                  <p>No Investor Accounts linked to this contact</p>
                  <p className="text-xs mt-1">Link this contact in an Investor Account's "Account Information" section to see data here</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Controls: Currency converter + Format selector */}
                  <div className="flex flex-wrap items-center justify-end gap-3">
                    {/* Always-on currency toggle (allows on-demand INR↔USD
                        conversion even when the contact only has funds in
                        one native currency). */}
                    <div className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-3 py-1.5">
                      <select value={isSelectedCurrency} onChange={e => setIsSelectedCurrency(e.target.value)} className="border-0 text-sm focus:outline-none focus:ring-0 bg-transparent" data-testid="contact-inv-currency-select">
                        <option value="INR">INR</option>
                        <option value="USD">USD</option>
                      </select>
                      {isSelectedCurrency === 'USD' && (
                        <>
                          <input type="number" value={isExchangeRate} onChange={e => setIsExchangeRate(parseFloat(e.target.value) || 1)} step="0.01" min="0" className="border-0 text-sm focus:outline-none focus:ring-0 w-16 bg-transparent" />
                          <span className="text-xs text-gray-500">INR/USD</span>
                        </>
                      )}
                    </div>
                    <div className="flex items-center bg-gray-100 rounded-lg p-0.5" data-testid="contact-inv-format-selector">
                      {['full', 'crores', 'millions'].map(f => (
                        <button key={f} onClick={() => setIsCurrencyFormat(f)}
                          className={`px-3 py-1 text-sm rounded-md transition-colors ${isCurrencyFormat === f ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}>
                          {f === 'full' ? 'Full' : f === 'crores' ? 'Crores' : 'Millions'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Grand Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="contact-inv-summary-cards">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                      <p className="text-xs text-blue-600 font-medium mb-1">Total Commitment</p>
                      <p className="text-lg font-bold text-blue-900" data-testid="contact-total-commitment">{fmtDisplay(grandTotals.commitment)}</p>
                    </div>
                    <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-lg p-4 border border-cyan-200">
                      <p className="text-xs text-cyan-700 font-medium mb-1">Total Called</p>
                      <p className="text-lg font-bold text-cyan-900" data-testid="contact-total-called">{fmtDisplay(grandTotals.called)}</p>
                    </div>
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                      <p className="text-xs text-green-600 font-medium mb-1">Total Contribution</p>
                      <p className="text-lg font-bold text-green-900" data-testid="contact-total-contribution">{fmtDisplay(grandTotals.contribution)}</p>
                    </div>
                    <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg p-4 border border-amber-200">
                      <p className="text-xs text-amber-600 font-medium mb-1">Total Distribution</p>
                      <p className="text-lg font-bold text-amber-900" data-testid="contact-total-distribution">{fmtDisplay(grandTotals.distribution)}</p>
                    </div>
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                      <p className="text-xs text-purple-600 font-medium mb-1">Total NAV</p>
                      <p className="text-lg font-bold text-purple-900" data-testid="contact-total-nav">{fmtDisplay(grandTotals.nav)}</p>
                    </div>
                  </div>

                  {/* Per-Account Tables */}
                  {investmentSummary.map(acct => {
                    const allInvs = acct.investments || [];
                    const acctTotals = allInvs.reduce((acc, inv) => ({
                      commitment: acc.commitment + convertAmt(inv.commitment_amount || 0, inv.fund_currency),
                      called: acc.called + convertAmt(inv.called_amount || 0, inv.fund_currency),
                      contribution: acc.contribution + convertAmt(inv.contribution_amount || 0, inv.fund_currency),
                      distribution: acc.distribution + convertAmt(inv.distribution_amount || 0, inv.fund_currency),
                      nav: acc.nav + convertAmt(inv.nav_value || 0, inv.fund_currency),
                    }), { commitment: 0, called: 0, contribution: 0, distribution: 0, nav: 0 });

                    return (
                      <div key={acct.account_id} className="border rounded-lg overflow-hidden" data-testid={`inv-summary-account-${acct.account_id}`}>
                        <div className="bg-gray-50 px-4 py-3 border-b flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Briefcase size={16} className="text-gray-500" />
                            <h3 className="font-semibold text-gray-800">{acct.account_name}</h3>
                          </div>
                          <button onClick={() => navigate(`/investors/accounts/${acct.account_id}`)} className="text-xs text-blue-600 hover:text-blue-800 font-medium">View Account</button>
                        </div>
                        {allInvs.length === 0 ? (
                          <p className="text-gray-400 text-sm p-4">No fund investments in this account</p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="bg-gray-50 border-b text-left text-gray-600 font-medium">
                                  <th className="px-4 py-2">Fund Name</th>
                                  <th className="px-4 py-2">Currency</th>
                                  <th className="px-4 py-2 text-right">Commitment</th>
                                  <th className="px-4 py-2 text-right">Called</th>
                                  <th className="px-4 py-2 text-right">Contribution</th>
                                  <th className="px-4 py-2 text-right">Distribution</th>
                                  <th className="px-4 py-2 text-right">NAV</th>
                                  <th className="px-4 py-2 text-right">Investor IRR (%)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {allInvs.map((inv, idx) => (
                                  <tr key={idx} className="border-b hover:bg-gray-50">
                                    <td className="px-4 py-2 font-medium">{inv.fund_name}</td>
                                    <td className="px-4 py-2 text-gray-500">{inv.fund_currency || '-'}</td>
                                    <td className="px-4 py-2 text-right">{fmtAmt(inv.commitment_amount, inv.fund_currency)}</td>
                                    <td className="px-4 py-2 text-right">{fmtAmt(inv.called_amount, inv.fund_currency)}</td>
                                    <td className="px-4 py-2 text-right">{fmtAmt(inv.contribution_amount, inv.fund_currency)}</td>
                                    <td className="px-4 py-2 text-right">{fmtAmt(inv.distribution_amount, inv.fund_currency)}</td>
                                    <td className="px-4 py-2 text-right">{fmtAmt(inv.nav_value, inv.fund_currency)}</td>
                                    <td className="px-4 py-2 text-right font-medium">{inv.investor_irr != null ? `${inv.investor_irr}%` : '-'}</td>
                                  </tr>
                                ))}
                                <tr className="bg-gray-100 font-semibold border-t-2 border-gray-300">
                                  <td className="px-4 py-2" colSpan={2}>Total</td>
                                  <td className="px-4 py-2 text-right">{fmtDisplay(acctTotals.commitment)}</td>
                                  <td className="px-4 py-2 text-right">{fmtDisplay(acctTotals.called)}</td>
                                  <td className="px-4 py-2 text-right">{fmtDisplay(acctTotals.contribution)}</td>
                                  <td className="px-4 py-2 text-right">{fmtDisplay(acctTotals.distribution)}</td>
                                  <td className="px-4 py-2 text-right">{fmtDisplay(acctTotals.nav)}</td>
                                  <td className="px-4 py-2"></td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

const InfoField = ({ label, value }) => (
  <div>
    <span className="text-sm text-gray-500">{label}</span>
    <p className="mt-1 text-gray-900 font-medium">{value || '-'}</p>
  </div>
);

export default InvestorContactDetails;
